/**
 * @file   src\components\Inventory\EditInventory\InventoryLocationWithMustHaves.js
 * @brief  This file is responsible for the view of inventory location with must haves.
 * @date   June, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useEffect, useState } from "react";
import Form from "react-bootstrap/Form";
import { capitalizeString, handleTextBoxChange } from "../../../actions/common";
import GeoLocation from "../../Common/GeoLocation";
import {
  checkIsNull,
  createS3Folder,
  replaceComma,
  thousandSeperatedValue,
} from "../../../helpers/common";
import TextBox from "../../Common/TextBox";
import {
  convertToNumber,
  removeNegativeInput,
} from "../../../helpers/validations";
import CheckBox from "../../Common/CheckBox";
import {
  adaAccessCheckBox,
  DEFAULT_INVENTORY_MAP_ZOOM,
  IS_APARTMENT_CONDO,
  IS_ENTIRE_PLACE,
  IS_GUEST_HOUSE,
  IS_PRIVATE_ROOM,
  newConstructionCheckBox,
} from "../../../constants/common";
import InventoryLocation from "../EditInventory/v2/InventoryLocation";

const InventoryLocationWithMustHaves = (props) => {
  const {
    handleFormValues,
    locationAddress,
    noBedRm,
    noBathRm,
    sqFt,
    stories,
    lotSize,
    grgeSpots,
    adaAccess,
    IsNewConstruction,
    propertyType,
    propertySubType,
    neighborHood,
    highSchool,
    yearBuilt,
    validationErrors,
    isEdit = false,
    lat,
    lng,
    mapZoom,
    isAgentCreated = false,
    s3Imagefolder,
    mapType,
    locationError,
    sessionKey,
  } = props;

  // Initialize component state variables and constants.
  const [yearBuiltError, setYearBuiltError] = useState("");
  const [isPropertyDataExist, setDataExistFlag] = useState(false);

  const defaultProps = {
    center: {
      lat: convertToNumber(lat),
      lng: convertToNumber(lng),
    },
    zoom: mapZoom || DEFAULT_INVENTORY_MAP_ZOOM,
  };

  const isLotSizeDisable =
    propertyType === IS_ENTIRE_PLACE &&
    (propertySubType === IS_APARTMENT_CONDO ||
      propertySubType === IS_GUEST_HOUSE);
  const isPrivateRoom = propertyType === IS_PRIVATE_ROOM;

  // To set the folder name for map image upload
  useEffect(() => {
    handleFormValues({
      isPropertyExist: false,
      mapZoom: mapZoom || DEFAULT_INVENTORY_MAP_ZOOM,
    });
    if (!locationAddress) {
      const s3Folder = s3Imagefolder;
      if (!s3Folder) {
        getFolderName();
      }
      handlePropertyExist({ isExist: false });
    } else {
      handlePropertyExist({ isExist: true });
    }
  }, []);

  // validationErrors state change side effect handling.
  useEffect(() => {
    setYearBuiltError("");
    if (validationErrors && validationErrors.length > 0) {
      validationErrors.map((item) => {
        if (item == "yearBuiltRange") {
          setYearBuiltError("Enter Valid year");
        }
      });
    }
  }, [validationErrors]);

  // To get the folder name Api call
  const getFolderName = async (userId) => {
    const isWantAd = false;
    await createS3Folder(isWantAd).then(function (result) {
      const folderName = result;
      handleFormValues({ s3ImageFolder: folderName });
    });
  };

  // To update the state values
  const updateValuesToState = (key, value) => {
    if (!checkIsNull(value)) {
      handleFormValues({ [key]: value });
    } else {
      handleFormValues({ [key]: "" });
    }
  };

  // bed room change event handler
  const handleBedRoomChange = (e) => {
    let value = removeNegativeInput(handleTextBoxChange(e));
    if (value === "0") {
      value = 1;
    } else if (replaceComma(value) > 100) {
      value = 100;
    }
    updateValuesToState("noBedRm", replaceComma(value));
  };

  // bathroom change event handler
  const handleBathRoomChange = (e) => {
    let value = removeNegativeInput(handleTextBoxChange(e));
    if (value === "0") {
      value = 1;
    } else if (replaceComma(value) > 100) {
      value = 100;
    }
    updateValuesToState("noBathRm", replaceComma(value));
  };

  // square feet chage event handler
  const handleSqFtChange = (e) => {
    let value = removeNegativeInput(handleTextBoxChange(e));
    if (value === "0") {
      value = 1;
    } else if (replaceComma(value) > 10000) {
      value = 10000;
    }
    updateValuesToState("sqFt", replaceComma(value));
  };

  // To handle the stories change
  const handleStoryChange = (e) => {
    const value = removeNegativeInput(handleTextBoxChange(e));
    updateValuesToState("stories", replaceComma(value));
  };

  // lot size chnage handler
  const handleLotSizeChange = (e) => {
    const value = removeNegativeInput(handleTextBoxChange(e));
    updateValuesToState("lotSize", replaceComma(value));
  };

  // garrage spot change handler
  const handleGarrageSpotChange = (e) => {
    const value = removeNegativeInput(handleTextBoxChange(e));
    updateValuesToState("grgeSpots", replaceComma(value));
  };

  // year built change handler
  const handleYearBuitChange = (e) => {
    setYearBuiltError("");
    const value = removeNegativeInput(handleTextBoxChange(e));
    updateValuesToState("yearBuilt", value);
  };

  // neighborhood change handler
  const handleNeighborhoodChange = (e) => {
    const value = capitalizeString(handleTextBoxChange(e));
    updateValuesToState("neighborHood", value);
  };

  // high school change handler
  const handleHighSchoolChange = (e) => {
    const value = capitalizeString(handleTextBoxChange(e));
    updateValuesToState("highSchool", value);
  };

  // ada access and is new construction checkbox change event handler
  const handleCheckBoxClick = (e) => {
    if (e.target.id === adaAccessCheckBox) {
      handleFormValues({ adaAccess: e.target.checked });
    } else {
      handleFormValues({ IsNewConstruction: e.target.checked });
    }
  };

  // to set property exist state values
  const handlePropertyExist = ({ isExist }) => {
    setDataExistFlag(isExist);
    handleFormValues({ invalidLocationExist: true });
  };

  return (
    <>
      {!locationAddress && !lat && !lng && (
        <GeoLocation handleFormValues={handleFormValues} />
      )}
      <>
        <div className="wantad-location-wrap mb-3">
          <InventoryLocation
            defaultProps={defaultProps}
            locationAddress={locationAddress}
            handleFormValues={handleFormValues}
            updateValuesToState={updateValuesToState}
            handlePropertyExist={handlePropertyExist}
            isAgentCreated={isAgentCreated}
            validationErrors={validationErrors}
            isPrivateRoom={isPrivateRoom}
            mapType={mapType}
            locationError={locationError}
            sessionKey={sessionKey}
          ></InventoryLocation>
          <div
            className={
              isPrivateRoom
                ? "section-control mb-40"
                : isPropertyDataExist
                ? "section-control mb-40"
                : "disabled-section-control mb-40"
            }
          >
            <h6>Basics</h6>
            {!isPrivateRoom && (
              <div className="row">
                <div className="col-md-6">
                  <div className="common-input-wrap mb-2">
                    <Form.Label>Neighborhood</Form.Label>
                    <TextBox
                      type="text"
                      className="form-control"
                      disabled={!isPropertyDataExist}
                      value={neighborHood}
                      onChange={(e) => {
                        handleNeighborhoodChange(e);
                      }}
                    ></TextBox>
                    <div className="error-msg"></div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="common-input-wrap mb-2">
                    <Form.Label>High School</Form.Label>
                    <TextBox
                      type="text"
                      className="form-control"
                      disabled={!isPropertyDataExist}
                      value={highSchool}
                      onChange={(e) => {
                        handleHighSchoolChange(e);
                      }}
                    ></TextBox>
                    <div className="error-msg"></div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="common-input-wrap mb-2">
                    <Form.Label>Bedrooms</Form.Label>
                    <TextBox
                      disabled={!isPropertyDataExist}
                      type="text"
                      className="form-control"
                      value={thousandSeperatedValue(noBedRm) || ""}
                      onChange={(e) => {
                        handleBedRoomChange(e);
                      }}
                    ></TextBox>
                    <div className="error-msg"></div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="common-input-wrap mb-2">
                    <Form.Label>Bathrooms</Form.Label>
                    <TextBox
                      type="text"
                      className="form-control"
                      disabled={!isPropertyDataExist}
                      value={thousandSeperatedValue(noBathRm) || ""}
                      onChange={(e) => {
                        handleBathRoomChange(e);
                      }}
                    ></TextBox>
                    <div className="error-msg"></div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="common-input-wrap mb-2">
                    <Form.Label>Square footage</Form.Label>
                    <TextBox
                      type="text"
                      className="form-control"
                      disabled={!isPropertyDataExist}
                      value={thousandSeperatedValue(sqFt) || ""}
                      onChange={(e) => {
                        handleSqFtChange(e);
                      }}
                    ></TextBox>
                    <div className="error-msg"></div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="common-input-wrap mb-2">
                    <Form.Label>Stories</Form.Label>

                    <TextBox
                      type="text"
                      className="form-control"
                      disabled={!isPropertyDataExist}
                      value={thousandSeperatedValue(stories) || ""}
                      onChange={(e) => {
                        handleStoryChange(e);
                      }}
                    ></TextBox>
                    <div className="error-msg"></div>
                  </div>
                </div>
                {!isLotSizeDisable && (
                  <div className="col-md-3">
                    <div className="common-input-wrap mb-2">
                      <Form.Label>Lot size (Acres)</Form.Label>

                      <TextBox
                        type="text"
                        className="form-control"
                        disabled={!isPropertyDataExist}
                        value={thousandSeperatedValue(lotSize) || ""}
                        onChange={(e) => {
                          handleLotSizeChange(e);
                        }}
                      ></TextBox>
                      <div className="error-msg"></div>
                    </div>
                  </div>
                )}

                <div className="col-md-3">
                  <div className="common-input-wrap mb-2">
                    <Form.Label>Garage Spots</Form.Label>
                    <TextBox
                      type="text"
                      className="form-control"
                      disabled={!isPropertyDataExist}
                      value={thousandSeperatedValue(grgeSpots) || ""}
                      onChange={(e) => {
                        handleGarrageSpotChange(e);
                      }}
                    ></TextBox>
                    <div className="error-msg"></div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="common-input-wrap mb-2">
                    <Form.Label>Year Built</Form.Label>
                    <TextBox
                      type="text"
                      className="form-control"
                      disabled={!isPropertyDataExist}
                      errorMessage={yearBuiltError}
                      value={yearBuilt}
                      onChange={(e) => {
                        handleYearBuitChange(e);
                      }}
                    ></TextBox>
                  </div>
                </div>
              </div>
            )}

            <div className="row">
              {propertyType !== IS_PRIVATE_ROOM && (
                <div className="col-md-4">
                  <div className="field-wrap mt-0 mb-0">
                    <label
                      className="form-check-label checkbox-label"
                      htmlFor={newConstructionCheckBox}
                    >
                      <CheckBox
                        className="form-check-input"
                        type="checkbox"
                        disabled={!isPropertyDataExist}
                        id={newConstructionCheckBox}
                        checked={IsNewConstruction}
                        onChange={(e) => handleCheckBoxClick(e)}
                      />{" "}
                      New Construction
                    </label>
                  </div>
                </div>
              )}

              <div className="col-md-8">
                <div className="field-wrap mt-0 mb-0">
                  <label
                    className="form-check-label checkbox-label"
                    htmlFor={adaAccessCheckBox}
                  >
                    <CheckBox
                      className="form-check-input"
                      type="checkbox"
                      id={adaAccessCheckBox}
                      checked={adaAccess}
                      onChange={(e) => handleCheckBoxClick(e)}
                    />{" "}
                    ADA Accessible
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    </>
  );
};

export default InventoryLocationWithMustHaves;
